import axios from 'axios';
import { migrateLegacySession, finishLegacyMigration } from './browserSession';
import { normalizeApiError, prepareManagedRequest, releaseManagedRequest } from './requestManager';

const configuredBackendUrl = String(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const runtimeOrigin = typeof window !== 'undefined' ? window.location.origin : '';
const runtimeHostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
// Production IVHR is served behind the same nginx origin. Always prefer ivhr.in
// there so a stale Jenkins REACT_APP_BACKEND_URL cannot send users to a legacy API.
const BACKEND_URL = ['ivhr.in', 'www.ivhr.in'].includes(runtimeHostname)
  ? runtimeOrigin
  : (configuredBackendUrl || runtimeOrigin);
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    'X-IVHR-Client': 'web'
  }
});

// HttpOnly cookies authenticate browser requests. Migrate old sessions once.
api.interceptors.request.use(async (config) => {
  const requestUrl = config.url || '';
  const isPublicAuthRequest = requestUrl.includes('/auth/login') || requestUrl.includes('/super-admin/login') || requestUrl.includes('/captcha/');
  if (requestUrl.includes('/auth/login') || requestUrl.includes('/super-admin/login')) {
    await finishLegacyMigration();
  }
  if (!isPublicAuthRequest) {
    await migrateLegacySession((token) => axios.post(`${API}/auth/session`, {}, {
      withCredentials: true,
      timeout: 30000,
      headers: { Authorization: `Bearer ${token}`, 'X-IVHR-Client': 'web' }
    }));
  }
  config = prepareManagedRequest(config);
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => {
    releaseManagedRequest(response.config);
    return response;
  },
  (error) => {
    releaseManagedRequest(error.config);
    const requestUrl = error.config?.url || '';
    const isPublicAuthRequest = requestUrl.includes('/auth/login') || requestUrl.includes('/super-admin/login') || requestUrl.includes('/captcha/');
    if (error.response?.status === 401 && !isPublicAuthRequest && !error.config?.skipAuthRedirect) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(normalizeApiError(error));
  }
);

export const auth = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me', { skipAuthRedirect: true }),
  logout: () => api.post('/auth/logout'),
  uploadProfilePhoto: (formData) => api.post('/profile/photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getProfilePhoto: () => api.get('/profile/photo', { responseType: 'blob' }),
  changePassword: (data) => api.post('/auth/change-password', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  verifyResetToken: (token) => api.get(`/auth/verify-reset-token/${token}`)
};

export const companies = {
  register: (data) => api.post('/companies/register', data),
  getByCode: (code) => api.get(`/companies/${code}`),
  uploadLogo: (companyId, formData) => api.post(`/companies/upload-logo/${companyId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getDocuments: (companyId) => api.get(`/super-admin/companies/${companyId}/documents`),
  uploadDocuments: (companyId, formData) => api.post(`/super-admin/companies/${companyId}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadDocument: (documentId) => api.get(`/super-admin/company-documents/${documentId}/download`, { responseType: 'blob' })
};

export const company = {
  getSettings: () => api.get('/company-settings'),
  updateSettings: (data) => api.put('/company-settings', data),
  getFullConfig: () => api.get('/admin/config/full-settings'),
  getAuditLogs: () => api.get('/admin/config/audit-logs')
};

export const roles = {
  getAll: () => api.get('/masters/roles'),
  create: (data) => api.post('/masters/roles', data),
  update: (id, data) => api.put(`/masters/roles/${id}`, data),
  delete: (id) => api.delete(`/masters/roles/${id}`)
};

export const captcha = {
  generate: () => api.get('/captcha/generate')
};

export const employees = {
  getAll: (config = {}) => api.get('/employees', config),
  getOne: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  downloadBulkTemplate: () => api.get('/employees/bulk-template', { responseType: 'blob' }),
  uploadBulk: (formData) => api.post('/employees/bulk-upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  activate: (id) => api.put(`/employees/${id}/activate`),
  deactivate: (id) => api.delete(`/employees/${id}`),
  getWorkingHours: (id, params) => api.get(`/employees/${id}/working-hours`, { params }),
  createWorkingHours: (id, data) => api.post(`/employees/${id}/working-hours`, data),
  updateWorkingHours: (id, workingHoursId, data) => api.put(`/employees/${id}/working-hours/${workingHoursId}`, data),
  getWorkingHoursHistory: (id) => api.get(`/employees/${id}/working-hours/history`),
  uploadDocument: (employeeId, formData) => api.post(`/employees/${employeeId}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getDocuments: (employeeId) => api.get(`/employees/${employeeId}/documents`),
  downloadDocument: (employeeId, documentId) => api.get(`/employees/${employeeId}/documents/${documentId}/download`, { responseType: 'blob' }),
  deleteDocument: (employeeId, documentId) => api.delete(`/employees/${employeeId}/documents/${documentId}`),
  getMyDocuments: () => api.get('/mobile/my-documents'),
  downloadMyDocument: (documentId) => api.get(`/mobile/my-documents/${encodeURIComponent(documentId)}/download`, { responseType: 'blob' })
};

export const attendance = {
  getAll: (params, config = {}) => api.get('/attendance', { ...config, params }),
  downloadReport: (params) => api.get('/attendance/export', { params, responseType: 'blob' }),
  mark: (data) => api.post('/attendance/mark', data),
  update: (id, data) => api.put(`/attendance/${id}`, data),
  convert: (id, newStatus) => api.put(`/attendance/${id}/convert`, { new_status: newStatus }),
  bulkMark: (data) => api.post('/attendance/bulk-mark', data),
  getSummary: (params, config = {}) => api.get('/attendance/summary', {
    ...config,
    params
  }),
  getLocationSettings: () => api.get('/attendance/location-settings'),
  updateLocationSettings: (data) => api.put('/attendance/location-settings', data),
  getLocationPrivileges: () => api.get('/attendance/location-privileges'),
  updateLocationPrivilege: (data) => api.put('/attendance/location-privileges', data),
  getPhotoLocationPolicies: () => api.get('/attendance/photo-location-policies'),
  getProofPhoto: (filename) => api.get(`/attendance/proof-photo/${filename}`, { responseType: 'blob' }),
  getPolicy: () => api.get('/attendance/policy'),
  getApiIntegration: () => api.get('/attendance/api-integration'),
  getSources: () => api.get('/attendance/sources'),
  getAttendanceSyncScheduler: () => api.get('/admin/config/attendance-sync-scheduler'),
  assignSource: (data) => api.post('/attendance/source-assignments', data),
  deleteSourceAssignment: (id) => api.delete(`/attendance/source-assignments/${id}`),
  syncApiAttendance: (data) => api.post('/attendance/api-sync', data),
  getApiSyncLogs: (params) => api.get('/attendance/api-sync/logs', { params }),
  getUnmapped: () => api.get('/attendance/unmapped'),
  mapUnmapped: (id, data) => api.put(`/attendance/unmapped/${id}/map`, data),
  // CompOff
  getCompOffs: (params) => api.get('/attendance/compoff', { params }),
  applyCompOff: (data) => api.post('/attendance/compoff', data),
  approveCompOff: (id, data = {}) => api.put(`/attendance/compoff/${id}/approve`, data),
  rejectCompOff: (id, data = {}) => api.put(`/attendance/compoff/${id}/reject`, data),
  cancelCompOff: (id, data) => api.put(`/attendance/compoff/${id}/cancel`, data),
  getCompOffConfig: () => api.get('/attendance/compoff/config'),
  updateCompOffConfig: (data) => api.put('/attendance/compoff/config', data),
  evaluateCompOffs: (data = {}) => api.post('/attendance/compoff/evaluate', data),
  getCompOffLedger: (params) => api.get('/attendance/compoff/ledger', { params }),
  getCompOffEligibleDates: (employeeId) => api.get('/attendance/compoff-eligible-dates', { params: { employee_id: employeeId } }),
  // Week-Off Config
  getWeekOffConfig: () => api.get('/attendance/weekoff-config'),
  setWeekOffConfig: (data) => api.post('/attendance/weekoff-config', data),
  createWeekOffConfig: (data) => api.post('/attendance/weekoff-config', data),
  updateWeekOffConfig: (id, data) => api.put(`/attendance/weekoff-config/${id}`, data),
  deleteWeekOffConfig: (id) => api.delete(`/attendance/weekoff-config/${id}`),
  getEmployeeWeekOffCalendar: (params) => api.get('/attendance/employee-weekoffs', { params }),
  updateEmployeeWeekOffCalendar: (employeeId, data) => api.put(`/attendance/employee-weekoffs/${employeeId}`, data),
  getGracePolicy: (params) => api.get('/attendance/grace-policy', { params }),
  createGracePolicy: (data) => api.post('/attendance/grace-policy', data),
  updateGracePolicy: (id, data) => api.put(`/attendance/grace-policy/${id}`, data),
  getGraceHistory: (params) => api.get('/attendance/grace-history', { params }),
  getCalculationDetails: (attendanceId) => api.get(`/attendance/${attendanceId}/calculation-details`),
  recalculate: (id, data) => api.post(`/attendance/${id}/recalculate`, data),
  overrideGrace: (id, data) => api.post(`/attendance/grace/${id}/override`, data),
  decideGrace: (id, data) => api.post(`/attendance/grace/${id}/manager-decision`, data),
  // Auto Geofence API
  getAutoGeofenceConfig: () => api.get('/attendance/auto-geofence/config'),
  saveAutoGeofenceConfig: (data) => api.post('/attendance/auto-geofence/config', data),
  getAutoGeofenceAssignments: (params) => api.get('/attendance/auto-geofence/assignments', { params }),
  saveAutoGeofenceAssignment: (data) => api.post('/attendance/auto-geofence/assignments', data),
  deleteAutoGeofenceAssignment: (id) => api.delete(`/attendance/auto-geofence/assignments/${id}`),
  getAutoGeofenceLiveStatus: () => api.get('/attendance/auto-geofence/live-status'),
  getAutoGeofenceHistory: (employeeId, date) => api.get(`/attendance/auto-geofence/history/${employeeId}${date ? `/${date}` : ''}`),
  getAutoGeofenceAdminHistory: (params) => api.get('/attendance/auto-geofence/admin/history', { params })
};

export const attendanceCorrections = {
  getAll: (params, config = {}) => api.get('/attendance-corrections', { ...config, params }),
  create: (data) => api.post('/attendance-corrections', data),
  edit: (id, data) => api.put(`/attendance-corrections/${id}`, data),
  approve: (id, data = {}) => api.put(`/attendance-corrections/${id}/approve`, data),
  reject: (id, data = {}) => api.put(`/attendance-corrections/${id}/reject`, data),
  cancel: (id) => api.put(`/attendance-corrections/${id}/cancel`)
};

export const leaves = {
  preview: (data) => api.post('/leaves/preview', data),
  edit: (id, data) => api.put(`/leaves/${id}`, data),
  getAll: (params, config = {}) => api.get('/leaves', { ...config, params }),
  apply: (data) => api.post('/leaves', data),
  approve: (id, data = {}) => api.put(`/leaves/${id}/approve`, data),
  reject: (id, data = {}) => api.put(`/leaves/${id}/reject`, data),
  cancel: (id, data = {}) => api.put(`/leaves/${id}/cancel`, data),
  approveCancellation: (id, data = {}) => api.put(`/leaves/${id}/cancellation/approve`, data),
  rejectCancellation: (id, data = {}) => api.put(`/leaves/${id}/cancellation/reject`, data),
  getDailyLeaveSummaryConfig: () => api.get('/leaves/daily-summary/config'),
  updateDailyLeaveSummaryConfig: (dailySummary, tomorrowNotice) => api.put('/leaves/daily-summary/config', { daily_summary: dailySummary, tomorrow_notice: tomorrowNotice }),
  getDailyLeaveSummaryHistory: (limit = 100) => api.get(`/leaves/daily-summary/history?limit=${limit}`),
  getDailyLeaveSummaryAudit: (limit = 200) => api.get(`/leaves/daily-summary/audit?limit=${limit}`),
  runDailyLeaveSummaryNow: (summaryDate) => api.post(`/leaves/daily-summary/run-now${summaryDate ? `?summary_date=${summaryDate}` : ''}`),
  getBalance: (employeeId, config = {}) => api.get(`/leaves/balance/${employeeId}`, config),
  getBalancesBatch: (employeeIds, year, config = {}) => api.post('/leaves/balances/batch', { employee_ids: employeeIds, year }, config),
  updateBalance: (employeeId, balances) => api.put(`/leaves/balance/${employeeId}`, balances),
  downloadBalanceSummary: (year) => api.get('/leaves/balance-summary/export', { params: { year }, responseType: 'blob' }),
  downloadOpeningBalanceTemplate: (year) => api.get('/leaves/opening-balance/template', { params: { year }, responseType: 'blob' }),
  uploadOpeningBalances: (formData, year) => api.post('/leaves/opening-balance/upload', formData, {
    params: { year },
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  syncBalances: () => api.post('/leaves/sync-balances')
};

export const salary = {
  create: (data) => api.post('/salary-structures', data),
  getAll: () => api.get('/salary-structures'),
  get: (employeeId) => api.get(`/salary-structures/${employeeId}`),
  update: (salaryId, data) => api.put(`/salary-structures/${salaryId}`, data),
  delete: (salaryId) => api.delete(`/salary-structures/${salaryId}`),
  getComponents: (includeInactive = false) => api.get('/salary-components', { params: { include_inactive: includeInactive } }),
  createComponent: (data) => api.post('/salary-components', data),
  updateComponent: (id, data) => api.put(`/salary-components/${id}`, data),
  getVersions: (employeeId) => api.get('/salary-structure-versions', { params: employeeId ? { employee_id: employeeId } : {} }),
  createVersion: (data) => api.post('/salary-structure-versions', data),
  previewVersion: (data) => api.post('/salary-structure-versions/preview', data)
};

export const payroll = {
  generate: (data) => api.post('/payroll/generate', data),
  getAll: (params) => api.get('/payroll', { params }),
  download: (id) => api.get(`/payroll/${id}/download`),
  createRun: (data) => api.post('/payroll-runs', data),
  updateRun: (id, data) => api.put(`/payroll-runs/${id}`, data),
  getRuns: () => api.get('/payroll-runs'),
  getRun: (id) => api.get(`/payroll-runs/${id}`),
  validateRun: (id) => api.post(`/payroll-runs/${id}/validate`),
  acknowledgeWarnings: (id, data) => api.post(`/payroll-runs/${id}/acknowledge-warnings`, data),
  calculateBatch: (id, data = {}) => api.post(`/payroll-runs/${id}/calculate-batch`, data),
  recalculate: (id, data) => api.post(`/payroll-runs/${id}/recalculate`, data),
  submitReview: (id) => api.post(`/payroll-runs/${id}/submit-review`),
  getWorkflowConfig: () => api.get('/payroll-workflow/config'),
  updateWorkflowConfig: (data) => api.put('/payroll-workflow/config', data),
  approvalInbox: () => api.get('/payroll-approval-inbox'),
  workflow: (id) => api.get(`/payroll-runs/${id}/workflow`),
  compareRuns: (id, againstId) => api.get(`/payroll-runs/${id}/compare/${againstId}`),
  reviewRun: (id, data = {}) => api.post(`/payroll-runs/${id}/review`, data),
  approveRun: (id, data = {}) => api.post(`/payroll-runs/${id}/approve`, data),
  lockRun: (id, data = {}) => api.post(`/payroll-runs/${id}/lock`, data),
  rejectRun: (id, data) => api.post(`/payroll-runs/${id}/reject`, data),
  reopenRun: (id, data) => api.post(`/payroll-runs/${id}/reopen`, data),
  payRun: (id, data) => api.post(`/payroll-runs/${id}/pay`, data),

  getStatutoryConfigurations: (params = {}) => api.get('/statutory-configurations', { params }),
  createStatutoryConfiguration: (data) => api.post('/statutory-configurations', data),
  supersedeStatutoryConfiguration: (id, data) => api.put(`/statutory-configurations/${id}`, data),
  previewStatutoryCalculation: (data) => api.post('/statutory-calculation-preview', data),
  getInputs: (params = {}) => api.get('/payroll-inputs', { params }),
  createInput: (data) => api.post('/payroll-inputs', data),
  downloadInputTemplate: (format = 'xlsx') => api.get('/payroll-inputs/template', { params: { format }, responseType: 'blob' }),
  previewInputImport: (formData) => api.post('/payroll-inputs/import/preview', formData),
  getInputHistory: () => api.get('/payroll-inputs/import-history'),
  getInputImport: (id) => api.get(`/payroll-inputs/import/${id}`),
  commitInputImport: (id) => api.post(`/payroll-inputs/import/${id}/commit`),
  reverseInputImport: (id, data) => api.post(`/payroll-inputs/import/${id}/reverse`, data),
  getLoans: (params = {}) => api.get('/employee-loans', { params }),
  getLoan: (id) => api.get(`/employee-loans/${id}`),
  createLoan: (data) => api.post('/employee-loans', data),
  changeLoan: (id, action, data = {}) => api.post(`/employee-loans/${id}/${action}`, data),
  previewArrears: (data) => api.post('/payroll-arrears/preview', data),
  commitArrears: (id) => api.post(`/payroll-arrears/${id}/commit`),
  getPayslipCandidates: (params = {}) => api.get('/payslips/candidates', { params }),
  getPayslips: (params = {}) => api.get('/payslips', { params }),
  previewPayslip: (data) => api.post('/payslips/preview', data),
  getPayslipPreview: (id) => api.get(`/payslips/${id}/preview`),
  downloadPayslipPdf: (id) => api.get(`/payslips/${id}/pdf`, { responseType: 'blob' }),
  bulkDownloadPayslips: (data) => api.post('/payslips/bulk-download', data, { responseType: 'blob' }),
  publishPayslip: (id, data) => api.post(`/payslips/${id}/publish`, data),
  emailPayslip: (id) => api.post(`/payslips/${id}/email`),
  getSalaryAccessGrants: () => api.get('/payslip-salary-access-grants'),
  createSalaryAccessGrant: (data) => api.post('/payslip-salary-access-grants', data),
  revokeSalaryAccessGrant: (id) => api.delete(`/payslip-salary-access-grants/${id}`),
  getBankTemplates: () => api.get('/bank-export-templates'),
  createBankTemplate: (data) => api.post('/bank-export-templates', data),
  getPaymentBatches: () => api.get('/payment-batches'),
  createPaymentBatch: (data) => api.post('/payment-batches', data),
  getPaymentBatch: (id) => api.get(`/payment-batches/${id}`),
  getBankAdvice: (id) => api.get(`/payment-batches/${id}/bank-advice`),
  exportBankTransfer: (id) => api.get(`/payment-batches/${id}/export`, { responseType: 'blob' }),
  recordPaymentAttempt: (batchId, itemId, data) => api.post(`/payment-batches/${batchId}/items/${itemId}/attempt`, data),
  reversePaymentBatch: (id, data) => api.post(`/payment-batches/${id}/reverse`, data),
  getGlMappings: () => api.get('/payroll-gl-mappings'),
  createGlMapping: (data) => api.post('/payroll-gl-mappings', data),
  getCostCentres: () => api.get('/payroll-cost-centre-mappings'),
  createCostCentre: (data) => api.post('/payroll-cost-centre-mappings', data),
  getPayrollJournals: () => api.get('/payroll-journals'),
  getPayrollJournal: (id) => api.get(`/payroll-journals/${id}`),
  createPayrollJournal: (data) => api.post('/payroll-journals', data),
  exportPayrollJournal: (id, format = 'xlsx') => api.get(`/payroll-journals/${id}/export`, { params: { format }, responseType: 'blob' }),
  getFullFinalSettlements: () => api.get('/full-final-settlements'),
  getMyFullFinalSettlements: () => api.get('/full-final-settlements/mine'),
  createFullFinalSettlement: (data) => api.post('/full-final-settlements', data),
  getFullFinalSettlement: (id) => api.get(`/full-final-settlements/${id}`),
  addFullFinalAdjustment: (id, data) => api.post(`/full-final-settlements/${id}/adjustments`, data),
  transitionFullFinal: (id, action, data = {}) => api.post(`/full-final-settlements/${id}/actions/${action}`, data),
  acknowledgeFullFinal: (id, data = {}) => api.post(`/full-final-settlements/${id}/acknowledge`, data),
  downloadFullFinalStatement: (id) => api.get(`/full-final-settlements/${id}/statement.pdf`, { responseType: 'blob' }),
};

export const dashboard = {
  getOverview: (view = 'self', date) => api.get('/dashboard/overview', { params: { view, date } }),
  getSummary: () => api.get('/dashboard/summary'),
  getAttendanceChart: () => api.get('/dashboard/attendance-chart')
};

export const infoHub = {
  getDocuments: () => api.get('/info-hub/documents'),
  // Let the browser set Content-Type so the required multipart boundary is included.
  uploadDocument: (formData) => api.post('/info-hub/documents', formData),
  getFile: (filename) => api.get(`/info-hub/files/${encodeURIComponent(filename)}`, {
    responseType: 'blob'
  }),
  deleteDocument: (id) => api.delete(`/info-hub/documents/${id}`)
};

export const feeds = {
  getAll: (params = {}) => api.get('/feeds', { params }),
  create: (formData) => api.post('/feeds', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, data) => api.put(`/feeds/${id}`, data),
  delete: (id, data = {}) => api.delete(`/feeds/${id}`, { data }),
  like: (id) => api.post(`/feeds/${id}/like`),
  comment: (id, data) => api.post(`/feeds/${id}/comments`, data),
  getFile: (filename) => api.get(`/feeds/files/${filename}`, { responseType: 'blob' }),
  getCelebrationConfig: () => api.get('/feeds/celebrations/config'),
  updateCelebrationConfig: (data) => api.put('/feeds/celebrations/config', data),
  getCelebrationTemplates: () => api.get('/feeds/celebrations/templates'),
  updateCelebrationTemplate: (type, data) => api.put(`/feeds/celebrations/templates/${type}`, data),
  getCelebrationHistory: (params = {}) => api.get('/feeds/celebrations/history', { params }),
  getCelebrationEmails: (eventId) => api.get(`/feeds/celebrations/history/${eventId}/emails`),
  retryCelebrationEmails: (eventId) => api.post(`/feeds/celebrations/history/${eventId}/retry`),
  processCelebrations: (data = {}) => api.post('/feeds/celebrations/process', data)
};

export const reports = {
  attendance: (month, year) => api.get('/reports/attendance', { params: { month, year } }),
  payroll: (month, year) => api.get('/reports/payroll', { params: { month, year } }),
  pfStatement: (month, year) => api.get('/reports/pf-statement', { params: { month, year } }),
  esicStatement: (month, year) => api.get('/reports/esic-statement', { params: { month, year } }),
  bonusStatement: (year) => api.get('/reports/bonus-statement', { params: { year } }),
  gratuityStatement: () => api.get('/reports/gratuity-statement'),
  graceHistory: (params) => api.get('/attendance/grace-history', { params }),
  payrollCatalog: () => api.get('/payroll-reports/catalog'),
  generatePayroll: (code, params = {}) => api.get(`/payroll-reports/${code}`, { params }),
  exportPayroll: (code, format, params = {}) => api.get(`/payroll-reports/${code}/export`, { params: { ...params, format }, responseType: 'blob' })
};

export const leavePolicy = {
  getConfig: () => api.get('/admin-config/leave-policy'),
  save: (data) => api.post('/admin-config/leave-policy', data),
  resolveScope: (data) => api.post('/admin-config/leave-policy/resolve', data),
  getEffective: (employee_id, as_of) => api.get('/leave-policy/effective', { params: { employee_id, as_of } }),
  simulate: (data) => api.post('/admin-config/leave-policy/simulate', data),
  recalculate: (employeeId, data) => api.post(`/leaves/recalculate/${employeeId}`, data),
  getAudit: (employeeId) => api.get(`/leaves/calculation-audit/${employeeId}`),
};

export const weekoffConfig = {
  getAll: () => api.get('/weekoff-config'),
  create: (data) => api.post('/weekoff-config', data),
  update: (id, data) => api.put(`/weekoff-config/${id}`, data),
  delete: (id) => api.delete(`/weekoff-config/${id}`)
};

export const holidays = {
  getAll: (params) => api.get('/holidays', { params }),
  create: (data) => api.post('/holidays', data),
  update: (id, data) => api.put(`/holidays/${id}`, data),
  delete: (id) => api.delete(`/holidays/${id}`)
};

export const compoff = {
  getAll: (params) => api.get('/compoff', { params }),
  apply: (data) => api.post('/compoff', data),
  approve: (id, data = {}) => api.put(`/compoff/${id}/approve`, data),
  reject: (id, data = {}) => api.put(`/compoff/${id}/reject`, data)
};

export const users = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  getPermissions: (id) => api.get(`/users/${id}/permissions`),
  updatePermissions: (id, data) => api.put(`/users/${id}/permissions`, data),
  resetPassword: (id) => api.put(`/users/${id}/reset-password`),
  activate: (id) => api.put(`/users/${id}/activate`),
  deactivate: (id) => api.put(`/users/${id}/deactivate`),
  delete: (id) => api.delete(`/users/${id}`)
};

export const accessControl = {
  getSystemModules: () => api.get('/system/modules'),
  getSuperAdminModules: () => api.get('/super-admin/modules'),
  getCompanyModules: (companyId) => api.get(`/super-admin/companies/${companyId}/modules`),
  updateCompanyModules: (companyId, data) => api.put(`/super-admin/companies/${companyId}/modules`, data),
  getAttendanceApiConfig: (companyId) => api.get(`/super-admin/companies/${companyId}/attendance-api-config`),
  updateAttendanceApiConfig: (companyId, data) => api.put(`/super-admin/companies/${companyId}/attendance-api-config`, data),
  getAttendancePolicy: (companyId) => api.get(`/super-admin/companies/${companyId}/attendance-policy`),
  updateAttendancePolicy: (companyId, data) => api.put(`/super-admin/companies/${companyId}/attendance-policy`, data),
  getCompanyDepartments: (companyId) => api.get(`/super-admin/companies/${companyId}/departments`),
  getCompanyDivisions: (companyId, params) => api.get(`/super-admin/companies/${companyId}/divisions`, { params }),
  getAttendanceSources: (companyId) => api.get(`/super-admin/companies/${companyId}/attendance-sources`),
  getAttendanceServiceMasters: (params) => api.get('/super-admin/attendance-service-masters', { params }),
  createAttendanceServiceMaster: (data) => api.post('/super-admin/attendance-service-masters', data),
  updateAttendanceServiceMaster: (masterId, data) => api.put(`/super-admin/attendance-service-masters/${masterId}`, data),
  deleteAttendanceServiceMaster: (masterId) => api.delete(`/super-admin/attendance-service-masters/${masterId}`),
  getAttendanceSyncScheduler: () => api.get('/super-admin/attendance-sync-scheduler'),
  updateAttendanceSyncScheduler: (data) => api.put('/super-admin/attendance-sync-scheduler', data),
  runAttendanceSyncNow: () => api.post('/super-admin/attendance-sync-scheduler/run-now'),
  getAttendancePhotoLocationPolicies: (companyId) => api.get(`/super-admin/companies/${companyId}/attendance-photo-location-policies`),
  updateAttendancePhotoLocationPolicy: (companyId, data) => api.put(`/super-admin/companies/${companyId}/attendance-photo-location-policies`, data),
  deleteAttendancePhotoLocationPolicy: (companyId, policyId) => api.delete(`/super-admin/companies/${companyId}/attendance-photo-location-policies/${policyId}`),
  createAttendanceSource: (companyId, data) => api.post(`/super-admin/companies/${companyId}/attendance-sources`, data),
  updateAttendanceSource: (companyId, sourceId, data) => api.put(`/super-admin/companies/${companyId}/attendance-sources/${sourceId}`, data),
  deleteAttendanceSource: (companyId, sourceId) => api.delete(`/super-admin/companies/${companyId}/attendance-sources/${sourceId}`),
  updateAttendanceSourceStatus: (companyId, sourceId, data) => api.put(`/super-admin/companies/${companyId}/attendance-sources/${sourceId}/status`, data),
  syncAttendanceApi: (companyId, data) => api.post(`/super-admin/companies/${companyId}/attendance-api-sync`, data),
  getAttendanceApiLogs: (companyId, params) => api.get(`/super-admin/companies/${companyId}/attendance-api-logs`, { params }),
  getMyModules: () => api.get('/permissions/modules')
};

export const notifications = {
  getAll: (params) => api.get('/notifications', { params }),
  markRead: (id) => api.put(`/notifications/${id}/read`)
};

export const systemLogs = {
  get: (lines = 200) => api.get('/system-logs', { params: { lines } })
};

export const userGuide = {
  getLanguages: () => api.get('/user-guide/languages'),
  getMyGuides: (params) => api.get('/user-guide/my-guide', { params }),
  getPageGuide: (params) => api.get('/user-guide/page-guide', { params }),
  search: (params) => api.get('/user-guide/search', { params }),
  getFaq: (params) => api.get('/user-guide/faq', { params }),
  getVideos: (params) => api.get('/user-guide/videos', { params }),
  ask: (data) => api.post('/user-guide/ask', data),
  feedback: (data) => api.post('/user-guide/feedback', data),
  adminList: () => api.get('/user-guide/admin/guides'),
  adminCreate: (data) => api.post('/user-guide/admin/guides', data),
  adminUpdate: (id, data) => api.put(`/user-guide/admin/guides/${id}`, data),
  uploadFile: (id, file, kind) => { const data = new FormData(); data.append('file', file); data.append('kind', kind); return api.post(`/user-guide/admin/guides/${id}/files`, data); },
  removeFile: (guideId, fileId) => api.delete(`/user-guide/admin/guides/${guideId}/files/${fileId}`),
  downloadFile: (guideId, fileId) => api.get(`/user-guide/guides/${guideId}/files/${fileId}`, { responseType: 'blob' }),
  downloadAttachment: async (guideId, item) => { const response = await api.get(`/user-guide/guides/${guideId}/files/${item.file_id}`, { responseType: 'blob' }); const url = URL.createObjectURL(response.data); const link = document.createElement('a'); link.href = url; link.download = item.filename || 'attachment'; link.click(); URL.revokeObjectURL(url); }
};

// Masters API
export const masters = {
  // Roles
  getRoles: () => api.get('/masters/roles'),
  createRole: (data) => api.post('/masters/roles', data),
  updateRole: (id, data) => api.put(`/masters/roles/${id}`, data),
  deleteRole: (id) => api.delete(`/masters/roles/${id}`),
  
  getDepartments: () => api.get('/masters/departments'),
  createDepartment: (data) => api.post('/masters/departments', data),
  updateDepartment: (id, data) => api.put(`/masters/departments/${id}`, data),
  deleteDepartment: (id) => api.delete(`/masters/departments/${id}`),
  
  getRegions: () => api.get('/masters/regions'),
  createRegion: (data) => api.post('/masters/regions', data),
  updateRegion: (id, data) => api.put(`/masters/regions/${id}`, data),
  deleteRegion: (id) => api.delete(`/masters/regions/${id}`),
  
  getDesignations: () => api.get('/masters/designations'),
  createDesignation: (data) => api.post('/masters/designations', data),
  updateDesignation: (id, data) => api.put(`/masters/designations/${id}`, data),
  deleteDesignation: (id) => api.delete(`/masters/designations/${id}`),
  
  getDivisions: (department) => api.get('/masters/divisions', { params: department ? { department } : {} }),
  createDivision: (data) => api.post('/masters/divisions', data),
  updateDivision: (id, data) => api.put(`/masters/divisions/${id}`, data),
  deleteDivision: (id) => api.delete(`/masters/divisions/${id}`),
  
  getEmploymentTypes: () => api.get('/masters/employment-types'),
  createEmploymentType: (data) => api.post('/masters/employment-types', data),
  updateEmploymentType: (id, data) => api.put(`/masters/employment-types/${id}`, data),
  deleteEmploymentType: (id) => api.delete(`/masters/employment-types/${id}`),
  
  getZones: () => api.get('/masters/zones'),
  createZone: (data) => api.post('/masters/zones', data),
  updateZone: (id, data) => api.put(`/masters/zones/${id}`, data),
  deleteZone: (id) => api.delete(`/masters/zones/${id}`),
  
  getLeaveTypes: () => api.get('/masters/leave-types'),
  createLeaveType: (data) => api.post('/masters/leave-types', data),
  updateLeaveType: (id, data) => api.put(`/masters/leave-types/${id}`, data),
  deleteLeaveType: (id) => api.delete(`/masters/leave-types/${id}`)
};

// Tenant Admin Configurations API
export const adminConfig = {
  getFullSettings: () => api.get('/admin/config/full-settings'),
  getAuditLogs: () => api.get('/admin/config/audit-logs'),
  getAttendanceSources: () => api.get('/admin/config/attendance-sources'),
  createAttendanceSource: (data) => api.post('/admin/config/attendance-sources', data),
  updateAttendanceSource: (sourceId, data) => api.put(`/admin/config/attendance-sources/${sourceId}`, data),
  deleteAttendanceSource: (sourceId) => api.delete(`/admin/config/attendance-sources/${sourceId}`),
  updateAttendanceSourceStatus: (sourceId, data) => api.put(`/admin/config/attendance-sources/${sourceId}/status`, data),
  getAttendanceApiConfig: () => api.get('/admin/config/attendance-api-config'),
  updateAttendanceApiConfig: (data) => api.put('/admin/config/attendance-api-config', data),
  getAttendanceServiceMasters: (params) => api.get('/admin/config/attendance-service-masters', { params }),
  createAttendanceServiceMaster: (data) => api.post('/admin/config/attendance-service-masters', data),
  updateAttendanceServiceMaster: (masterId, data) => api.put(`/admin/config/attendance-service-masters/${masterId}`, data),
  deleteAttendanceServiceMaster: (masterId) => api.delete(`/admin/config/attendance-service-masters/${masterId}`),
  getAttendanceSyncScheduler: () => api.get('/admin/config/attendance-sync-scheduler'),
  runAttendanceSyncNow: (data) => api.post('/attendance/api-sync', data),
  getPhotoLocationPolicies: () => api.get('/admin/config/attendance-photo-location-policies'),
  updatePhotoLocationPolicy: (data) => api.put('/admin/config/attendance-photo-location-policies', data),
  deletePhotoLocationPolicy: (policyId) => api.delete(`/admin/config/attendance-photo-location-policies/${policyId}`),
  getModulePermissions: () => api.get('/admin/config/module-permissions'),
  updateModulePermissions: (data) => api.put('/admin/config/module-permissions', data),
  getDepartments: () => api.get('/admin/config/departments'),
  getDivisions: (department) => api.get('/admin/config/divisions', { params: department ? { department } : {} })
};

export default api;

